import { Button } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";
import { PageHeader } from "@/components/data-display/page-header";
import { TableState } from "@/components/data-display/table-state";
import { UnavailableValue } from "@/components/data-display/unavailable-value";

/**
 * TRS-01 Opportunity Queue — layout port of the uat prototype
 * (`.design-ref/prototype/app/(enterprise-wallet)/treasury/opportunities/page.tsx`
 * and `features/treasury/treasury.tsx#OpportunityQueue`): page header, the
 * section title with its open-count chip, the opportunity table with its eight
 * canonical columns, and the queue toolbar.
 *
 * No treasury backend exists in this environment (page-delivery-pipeline.md G6),
 * so the prototype's TRS_OPPORTUNITIES rows (ids, descriptions, estimated
 * yields, risk badges, quote-expiry state) are deliberately NOT rendered: the
 * open count shows the shared unavailable marker, the table shows its designed
 * empty state, and every toolbar control is disabled with the shared WIP reason.
 * The prototype's filter radio group and "Create Intent" modal are not ported:
 * both need rows that do not exist, and an unreachable dialog is not a state.
 */

const WIP_ACTION_HINT_ID = "treasury-opportunities-wip-action-hint";

const COLUMNS = [
  { key: "id", label: "ID" },
  { key: "type", label: "Type" },
  { key: "description", label: "Description" },
  { key: "yield", label: "Est. Yield" },
  { key: "risk", label: "Risk" },
  { key: "source", label: "Source" },
  { key: "status", label: "Status" },
] as const;

export default function TreasuryOpportunitiesPage() {
  return (
    <div>
      {/* Reason shared by every disabled control below, referenced via aria-describedby. */}
      <span id={WIP_ACTION_HINT_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </span>

      <PageHeader
        title="Opportunity Queue"
        subtitle="Actionable treasury opportunities: stake, lend, borrow, swap, liquidity provision, and rebalancing."
        actions={<WipHint />}
      />

      <p className="mb-5 text-sm text-muted">
        The opportunity queue has no backend in this environment yet, so no opportunity, yield,
        quote or risk assessment is listed.
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-base font-bold text-ink">Opportunity Queue</h2>
        <span className="inline-flex items-center rounded-full border border-info-border bg-info-soft px-2 py-0.5 text-[11px] font-bold text-info">
          <UnavailableValue />
        </span>
      </div>

      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              {COLUMNS.map((column) => (
                <th key={column.key} scope="col" className="px-4 py-2.5">
                  {column.label}
                </th>
              ))}
              <th scope="col" className="px-4 py-2.5 text-right">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            <TableState
              variant="empty"
              colSpan={COLUMNS.length + 1}
              title="Opportunity Queue is not available in this environment yet"
              description="Treasury opportunities and their quotes, yields, and risk assessments have no backend in this environment yet. This page will show real opportunities once the treasury domain is built."
            />
          </tbody>
        </Table>
      </TableWrapper>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          size="md"
          variant="secondary"
          disabled
          aria-expanded={false}
          title={WIP_ACTION_HINT}
          aria-describedby={WIP_ACTION_HINT_ID}
        >
          Filter by Type
        </Button>
        <Button
          size="md"
          variant="secondary"
          disabled
          aria-pressed={false}
          title={WIP_ACTION_HINT}
          aria-describedby={WIP_ACTION_HINT_ID}
        >
          Sort by Yield
        </Button>
        <Button
          size="md"
          variant="secondary"
          disabled
          title={WIP_ACTION_HINT}
          aria-describedby={WIP_ACTION_HINT_ID}
        >
          Dismiss Selected
        </Button>
      </div>
    </div>
  );
}
